import {
	type DrawingComment,
	type DrawingTaskObject,
	getPinKind,
	type PublicDrawingTaskSummary,
} from "./drawing-state";
import type { ThumbnailEntity } from "./entity-thumbnails";

/**
 * Entity render digest (PLA-471, docs/plans/entity-list-thumbnails.md).
 *
 * Pure, isomorphic (browser + Bun), synchronous: a hash of exactly the fields
 * of one Comment / Marking that change its thumbnail. Title, notes, assignee,
 * dates, labels (except the legacy measurement pin label) and the Marking
 * workflow state are excluded — Markings are drawn in one fixed color.
 *
 * Canonical form: key-sorted JSON of the render projection, with every byte
 * array replaced by `{"$bytes":<length>}` and its raw bytes streamed into the
 * hash after the JSON, in order of appearance. SHA-256 (pure JS by default;
 * the server injects node:crypto), first 128 bits as hex.
 *
 * Bump ENTITY_RENDER_DIGEST_VERSION whenever the projection or the encoding
 * changes (and update the golden vector test).
 */
export const ENTITY_RENDER_DIGEST_VERSION = 1;

const DIGEST_HEX_LENGTH = 32;

/** Render-relevant projection of one object (layerId, frame excluded). */
/** null and missing render the same; the web task clone drops nulls. */
const present = <T>(value: T | null | undefined): T | undefined =>
	value ?? undefined;

/**
 * The web task clone (`cloneTask`) turns a null/missing `bounds` or
 * `centroid` into `{}`: treat an empty object like absent.
 */
const presentNonEmpty = (value: unknown): unknown =>
	value && typeof value === "object" && Object.keys(value).length > 0
		? value
		: undefined;

const projectObject = (object: DrawingTaskObject): Record<string, unknown> => ({
	h: object.h,
	bounds: presentNonEmpty(object.bounds),
	centroid: presentNonEmpty(object.centroid),
	outline: present(object.outline),
	shapeType: present(object.shapeType),
	shapeData: present(object.shapeData),
	metadata: present(object.metadata),
	geometryBlob: present(object.geometryBlob),
});

const projectComment = (
	comment: DrawingComment,
): Record<string, unknown> | null => {
	const objects = Array.isArray(comment.objects) ? comment.objects : [];
	const isNote = comment.commentType === "note";
	if (objects.length === 0 && !(isNote && comment.pinPosition)) {
		return null;
	}
	return {
		t: "comment",
		v: comment.v,
		page: comment.page,
		commentType: comment.commentType,
		// Comment-state color tints CAD / text objects and pin borders.
		commentState: comment.commentState,
		...(isNote
			? {
					pinPosition: comment.pinPosition,
					// Legacy label-only measurement pins resolve their kind from labels.
					labelPinKind: getPinKind({ labels: comment.labels }),
				}
			: {}),
		objects: objects.map(projectObject),
	};
};

const projectMarking = (
	marking: PublicDrawingTaskSummary,
): Record<string, unknown> | null => {
	const objects = Array.isArray(marking.objects) ? marking.objects : [];
	if (objects.length === 0) {
		return null;
	}
	return {
		t: "marking",
		page: marking.page,
		objects: objects.map(projectObject),
	};
};

// ── Canonical serialization ──

const isByteArray = (value: unknown): value is Uint8Array =>
	value instanceof Uint8Array;

/**
 * Key-sorted JSON. Byte arrays become `{"$bytes":n}` placeholders and are
 * collected into `chunks`. Undefined values (and keys holding them) are
 * dropped, like JSON.stringify does.
 */
const canonicalize = (value: unknown, chunks: Uint8Array[]): string => {
	if (value === null || value === undefined) {
		return "null";
	}
	if (isByteArray(value)) {
		chunks.push(value);
		return `{"$bytes":${value.byteLength}}`;
	}
	if (ArrayBuffer.isView(value)) {
		const bytes = new Uint8Array(
			value.buffer,
			value.byteOffset,
			value.byteLength,
		);
		chunks.push(bytes);
		return `{"$bytes":${bytes.byteLength}}`;
	}
	if (Array.isArray(value)) {
		return `[${value.map((item) => canonicalize(item, chunks)).join(",")}]`;
	}
	if (typeof value === "object") {
		const record = value as Record<string, unknown>;
		const parts: string[] = [];
		for (const key of Object.keys(record).sort()) {
			const item = record[key];
			if (item === undefined || typeof item === "function") {
				continue;
			}
			parts.push(`${JSON.stringify(key)}:${canonicalize(item, chunks)}`);
		}
		return `{${parts.join(",")}}`;
	}
	if (typeof value === "number") {
		return Number.isFinite(value) ? JSON.stringify(value) : "null";
	}
	return JSON.stringify(value) ?? "null";
};

/**
 * Yjs-decoded blobs may arrive as plain number arrays; normalize to bytes so
 * both forms hash equal.
 */
const normalizeBlobs = (
	projection: Record<string, unknown>,
): Record<string, unknown> => {
	const objects = projection.objects as Array<Record<string, unknown>>;
	return {
		...projection,
		objects: objects.map((object) =>
			Array.isArray(object.geometryBlob)
				? {
						...object,
						geometryBlob: Uint8Array.from(object.geometryBlob as number[]),
					}
				: object,
		),
	};
};

/** SHA-256 of the concatenated chunks as lowercase hex. */
export type Sha256Hex = (chunks: readonly Uint8Array[]) => string;

/**
 * Digest of an entity's render-relevant fields; null when the entity cannot
 * be rendered (no page, nothing to draw). `sha256` must be a real SHA-256:
 * client and server digests have to agree.
 */
export const entityRenderDigest = (
	entity: ThumbnailEntity,
	sha256: Sha256Hex = sha256Hex,
): string | null => {
	const page =
		entity.type === "comment" ? entity.comment.page : entity.marking.page;
	if (page === null || page === undefined) {
		return null;
	}
	const projection =
		entity.type === "comment"
			? projectComment(entity.comment)
			: projectMarking(entity.marking);
	if (!projection) {
		return null;
	}
	const chunks: Uint8Array[] = [];
	const json = canonicalize(normalizeBlobs(projection), chunks);
	const header = new TextEncoder().encode(
		`entity-render-digest/v${ENTITY_RENDER_DIGEST_VERSION}\n${json}`,
	);
	return sha256([header, ...chunks]).slice(0, DIGEST_HEX_LENGTH);
};

// ── SHA-256 (FIPS 180-4), pure JS, streaming over byte chunks ──

const K = new Uint32Array([
	0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
	0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
	0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
	0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
	0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
	0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
	0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
	0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
	0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
	0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
	0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const compress = (
	state: Uint32Array,
	w: Uint32Array,
	bytes: Uint8Array,
	offset: number,
): void => {
	for (let i = 0; i < 16; i++) {
		const j = offset + i * 4;
		w[i] =
			((bytes[j] as number) << 24) |
			((bytes[j + 1] as number) << 16) |
			((bytes[j + 2] as number) << 8) |
			(bytes[j + 3] as number);
	}
	for (let i = 16; i < 64; i++) {
		const w15 = w[i - 15] as number;
		const w2 = w[i - 2] as number;
		const s0 =
			((w15 >>> 7) | (w15 << 25)) ^ ((w15 >>> 18) | (w15 << 14)) ^ (w15 >>> 3);
		const s1 =
			((w2 >>> 17) | (w2 << 15)) ^ ((w2 >>> 19) | (w2 << 13)) ^ (w2 >>> 10);
		w[i] = ((w[i - 16] as number) + s0 + (w[i - 7] as number) + s1) | 0;
	}
	let a = state[0] as number;
	let b = state[1] as number;
	let c = state[2] as number;
	let d = state[3] as number;
	let e = state[4] as number;
	let f = state[5] as number;
	let g = state[6] as number;
	let h = state[7] as number;
	for (let i = 0; i < 64; i++) {
		const S1 =
			((e >>> 6) | (e << 26)) ^
			((e >>> 11) | (e << 21)) ^
			((e >>> 25) | (e << 7));
		const ch = (e & f) ^ (~e & g);
		const t1 = (h + S1 + ch + (K[i] as number) + (w[i] as number)) | 0;
		const S0 =
			((a >>> 2) | (a << 30)) ^
			((a >>> 13) | (a << 19)) ^
			((a >>> 22) | (a << 10));
		const maj = (a & b) ^ (a & c) ^ (b & c);
		const t2 = (S0 + maj) | 0;
		h = g;
		g = f;
		f = e;
		e = (d + t1) | 0;
		d = c;
		c = b;
		b = a;
		a = (t1 + t2) | 0;
	}
	state[0] = ((state[0] as number) + a) | 0;
	state[1] = ((state[1] as number) + b) | 0;
	state[2] = ((state[2] as number) + c) | 0;
	state[3] = ((state[3] as number) + d) | 0;
	state[4] = ((state[4] as number) + e) | 0;
	state[5] = ((state[5] as number) + f) | 0;
	state[6] = ((state[6] as number) + g) | 0;
	state[7] = ((state[7] as number) + h) | 0;
};

/**
 * SHA-256 of the concatenation of `chunks`, as lowercase hex. Chunks are read
 * in place (no concatenation copy); only a partial block is buffered.
 */
export const sha256Hex = (chunks: readonly Uint8Array[]): string => {
	const state = new Uint32Array([
		0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
		0x1f83d9ab, 0x5be0cd19,
	]);
	const w = new Uint32Array(64);
	const block = new Uint8Array(64);
	let buffered = 0;
	let total = 0;

	for (const chunk of chunks) {
		total += chunk.byteLength;
		let offset = 0;
		if (buffered > 0) {
			const take = Math.min(64 - buffered, chunk.byteLength);
			block.set(chunk.subarray(0, take), buffered);
			buffered += take;
			offset = take;
			if (buffered < 64) {
				continue;
			}
			compress(state, w, block, 0);
			buffered = 0;
		}
		for (; offset + 64 <= chunk.byteLength; offset += 64) {
			compress(state, w, chunk, offset);
		}
		if (offset < chunk.byteLength) {
			block.set(chunk.subarray(offset), 0);
			buffered = chunk.byteLength - offset;
		}
	}

	// Padding: 0x80, zeros, 64-bit big-endian bit length.
	block[buffered++] = 0x80;
	if (buffered > 56) {
		block.fill(0, buffered);
		compress(state, w, block, 0);
		buffered = 0;
	}
	block.fill(0, buffered, 56);
	const bitsHigh = Math.floor(total / 0x20000000);
	const bitsLow = (total * 8) >>> 0;
	block[56] = bitsHigh >>> 24;
	block[57] = bitsHigh >>> 16;
	block[58] = bitsHigh >>> 8;
	block[59] = bitsHigh;
	block[60] = bitsLow >>> 24;
	block[61] = bitsLow >>> 16;
	block[62] = bitsLow >>> 8;
	block[63] = bitsLow;
	compress(state, w, block, 0);

	let hex = "";
	for (const word of state) {
		hex += word.toString(16).padStart(8, "0");
	}
	return hex;
};
